import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { FeatureDTO, FeatureFilterDTO, AttributeDTO, AttributeFilterDTO, Result, ApplicationFilterDTO, ApplicationDTO, AccessRoleDTO, AccessRoleFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { AttributeMaintenance } from './AttributeMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';

interface IAttributeProps {
    data: any;
}

export const Attribute: React.FC<IAttributeProps> = ({ data }) => {

    const [featureList, setFeatureList] = useState<FeatureDTO[]>([]);
    const [applicationId, setApplicationId] = useState('');
    const [applicationList, setApplicationList] = useState<ApplicationDTO[]>([]);
    const [attributeList, setAttributeList] = useState<AttributeDTO[]>([]);
    const [attributeFilterDTO, setAttributeFilterDTO] = useState<AttributeFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setAttributeFilterDTO({
            ...attributeFilterDTO, name: '',
            featureId: data.id
        } as AttributeFilterDTO);

        handleApplicationGetAllAsync();
        handleFeatureGetAllAsync();

        handleGetPermission(ListId.appsphere.attributeId, setAccessRolePermissionDTO);
    }, []);

    useEffect(() => {
        handleAttributeGetAllViewAsync();
    }, [attributeFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleAttributeGetAllViewAsync();
    }, [actionExecuted]);

    useEffect(() => {
        handleFeatureGetAllAsync();
    }, [applicationId]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);
    
    const handleApplicationGetAllAsync = useCallback(async () => {
        const applicationFilterDTO: ApplicationFilterDTO = {
            name: '',
            typeId: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('appsphere.application', applicationFilterDTO, setLoading, setApplicationList, showMessage);
    }, []);

    const handleFeatureGetAllAsync = useCallback(async () => {
        if (applicationId === '') {
            //setAttributeFilterDTO({ ...attributeFilterDTO, featureId: '' } as AttributeFilterDTO);
            setFeatureList([]);
            return;
        }
        const featureListDTO: FeatureFilterDTO = {
            applicationId: applicationId,
        };
        GenericService.GetAllAsync1('appsphere.feature', featureListDTO, setLoading, setFeatureList, showMessage);
    }, [applicationId]);

    const handleAttributeGetAllViewAsync = useCallback(async () => {
        if (attributeFilterDTO === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return; 
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage('There is no permission for get all view.', 'info');
            return;
        }
        GenericService.GetAllViewAsync1('appsphere.attribute', attributeFilterDTO, setActionExecuted, setLoading, setAttributeList, showMessage);
    }, [attributeFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setAttributeFilterDTO({ ...attributeFilterDTO, name: '' } as AttributeFilterDTO);
        setApplicationId('');
    };

    function handleSearchClick() {
        handleAttributeGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage('There is no permission for new record.', 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage('There is no permission for view details.', 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage('There is no permission for delete record.', 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Attribute', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: attributeList.length },
        { label: 'Name', value: attributeFilterDTO?.name },
        { label: 'Applicaton', value: applicationList.find(x => x.id === applicationId)?.name },
        { label: 'Feature', value: featureList.find(x => x.id === attributeFilterDTO?.featureId)?.name },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: 'Name', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'action', label: 'Action', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'defaultValue', label: 'Default Value', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'automationId', label: 'Automation Id', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'identificationType', label: 'Identification Type', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'orderExecution', label: 'Order Execution', size: 1, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='APPLICATION'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                setValue={setAttributeFilterDTO}
                                value={attributeFilterDTO?.name}
                                obj={attributeFilterDTO}
                                placeholder={'Name'}
                                maxLength={200}
                            />
                        </Grid>
                        {!data && (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='applicationId'
                                        label='Application Name'
                                        setValue={setApplicationId}
                                        value={applicationId}
                                        valueList={applicationList}
                                        hasNoneSelect={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='featureId'
                                        label='Feature Name'
                                        setValue={setAttributeFilterDTO}
                                        value={attributeFilterDTO?.featureId}
                                        obj={attributeFilterDTO}
                                        valueList={featureList}
                                        hasNoneSelect={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        )}
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <CardList
                    columns={columns}
                    rows={attributeList}
                    actions={cardActions}
                    hasChildren={false}
                    setRowExpanded={() => void undefined}
                    cardSize={12}
                />
                {newRecord && (
                    <AttributeMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        parentId={data.id ?? attributeFilterDTO?.featureId}
                        isAttributeValue={false}
                    />
                )}
                {editRecord && (
                    <AttributeMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        isAttributeValue={false}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'attribute'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};